/*
  paymentMath.js — the mortgage-payment engine shared by the calculators.

  WHY IT IS ITS OWN FILE
  The Payment Calculator and the Scenario Builder compute the same numbers. Two copies of the
  FHA tier tables would eventually disagree, and the way you'd find out is a borrower being
  quoted two different MI figures by two pages of the same app. One copy, imported by both.

  PROVENANCE
  The structure is ported from CGS2022 Scripts/Calculator/CalculatorV2.vue. The FHA rates follow
  HUD Mortgagee Letter 2023-05 and the VA rates follow the current VA funding fee table, not
  CGS: CGS still carries the pre-2023 MIP table, a 0.01% refi UFMIP, the retired Reserves/Guard
  fee tier and an IRRRL fee on cash-out refis. Other departures from CGS are noted where they occur.

  UNITS — the thing that silently produces plausible-but-wrong numbers:
    annualPct is a PERCENT (7.5), never a decimal.
    months is MONTHS (360), never years.
  CGS is inconsistent about this internally (V1 stores the rate as a decimal, V2 as a percent,
  and one helper divides by 100 while its neighbour does not). Everything here is percent.
*/

// ── rate constants (HUD ML 2023-05, VA funding fee table, USDA guarantee fee) ──
// UFMIP is the same 1.75% for a purchase and a refinance.
export const FHA_UPFRONT = 0.0175
export const FHA_HIGH_BALANCE_CUTOFF = 726200
export const USDA_UPFRONT = 0.01
export const USDA_MONTHLY = 0.0035
export const VA_REFI_RATE = 0.005    // IRRRL

export const LOAN_TYPES = [
  { key: 'conv', label: 'Conventional' },
  { key: 'fha', label: 'FHA' },
  { key: 'va', label: 'VA' },
  { key: 'usda', label: 'USDA' }
]
export const REFI_TYPES = ['Cash Out', 'No Cash Out']
export const VA_TYPES = ['Regular Military/Active Duty', 'Reserves', 'National Guard', 'Exempt']
// CGS ships this list with "Graduated Payament" misspelled. Fixed here.
export const AMORT_TYPES = ['Fixed', 'Adjustable Rate', 'Graduated Payment', 'Growing Equity Mortgage', 'Other']
// For names that have to fit the narrow scenario card on the PDF.
export const AMORT_SHORT = {
  'Adjustable Rate': 'ARM',
  'Graduated Payment': 'GPM',
  'Growing Equity Mortgage': 'GEM',
  Other: 'Other'
}
// The PDF's Amortization row, which also tells the borrower whether the payment can change.
export const AMORT_PDF_LABEL = {
  Fixed: 'Fixed',
  'Adjustable Rate': 'Adjustable rate – initial payment, can change',
  'Graduated Payment': 'Graduated payment – payment will change',
  'Growing Equity Mortgage': 'Growing equity – payment will change',
  Other: 'Other – initial payment, can change'
}

// Agency SOFR hybrid ARMs (Conventional only): fixed for `fixedMonths`, then reset every 6
// months. Caps are percentage points: initial / periodic / lifetime above the start rate.
export const ARM_PRODUCTS = [
  { key: '5/6', label: '5/6 ARM SOFR (2/1/5)', fixedMonths: 60, initialCap: 2, periodicCap: 1, lifetimeCap: 5 },
  { key: '7/6', label: '7/6 ARM SOFR (5/1/5)', fixedMonths: 84, initialCap: 5, periodicCap: 1, lifetimeCap: 5 },
  { key: '10/6', label: '10/6 ARM SOFR (5/1/5)', fixedMonths: 120, initialCap: 5, periodicCap: 1, lifetimeCap: 5 }
]
export const ARM_DEFAULT_PRODUCT = '7/6'
export const ARM_DEFAULT_MARGIN = 2.75
const ARM_RESET_MONTHS = 6
export const GPM_DEFAULT_RATE = 7.5
export const GPM_DEFAULT_YEARS = 5
export const GEM_DEFAULT_RATE = 3
const GRADED_MAX_RISE = 15
const ARM_MAX_PCT = 20

// Adjustable Rate is offered on Conventional only: the caps above are the agency SOFR ones, and
// government ARMs have their own.
export function amortTypesFor (loanType) {
  return loanType === 'conv' ? AMORT_TYPES : AMORT_TYPES.filter(a => a !== 'Adjustable Rate')
}

// Forgiving on purpose: the inputs hold whatever the user typed, commas and dollar signs and
// half-finished decimals included.
export function num (v) {
  const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

export function hasValue (v) {
  return String(v == null ? '' : v).trim() !== ''
}

function orDefault (v, d) {
  return hasValue(v) ? num(v) : d
}

// Tiers compare the LTV as it is displayed (3 dp). Unrounded, 5% down can compute to
// 95.0000000001 and land in the >95 tier.
export function roundLtv (ltv) {
  return Math.round(ltv * 1000) / 1000
}

// Standard amortizing payment. CGS puts .toFixed(2) on the DENOMINATOR rather than the result,
// which quietly rounded the divisor; corrected here.
export function pmt (principal, annualPct, months) {
  const r = annualPct / 100 / 12
  if (!(principal > 0 && months > 0)) return 0
  if (r <= 0) return principal / months   // a genuine 0% loan is straight-line
  const pow = Math.pow(1 + r, months)
  return (principal * r * pow) / (pow - 1)
}

// Index + margin to the nearest 0.125%. The 5-decimal pass strips float noise first, so a sum
// that is exactly an eighth's midpoint is not nudged to the wrong side.
export function fullyIndexedRate (indexPct, marginPct) {
  return Math.round(Math.round((num(indexPct) + num(marginPct)) * 1e5) / 1e5 * 8) / 8
}

/*
  The financed up-front fee: FHA UFMIP, the VA funding fee, or the USDA guarantee fee.

  `baseLtv` is the LTV BEFORE the fee is financed. CGS reads its `ltv` computed here, which is
  derived from `loanAmount`, which is derived from this fee — a real circular dependency that
  only resolved through Vue's side-effect ordering and a stale value. Taking the tier off the
  pre-fee loan is deterministic, and it is also how the fee actually works.

  VA: Reserves and National Guard pay the same table as Regular Military since 2020, so only
  Exempt branches. A "No Cash Out" refi is priced as an IRRRL; a cash-out refi pays the
  under-5%-down purchase rate whatever its LTV.
*/
export function upFrontFee ({ loanType, isBuy, refiType, vaType, firstTime, baseLoanAmount, baseLtv }) {
  const base = num(baseLoanAmount)
  if (base <= 0) return 0

  if (loanType === 'fha') return base * FHA_UPFRONT
  if (loanType === 'usda') return base * USDA_UPFRONT

  if (loanType === 'va') {
    if (vaType === 'Exempt') return 0
    const firstOrSubsequent = firstTime ? 0.0215 : 0.033
    if (!isBuy) return base * (refiType === 'Cash Out' ? firstOrSubsequent : VA_REFI_RATE)

    const ltv = num(baseLtv)
    return base * (ltv > 95 ? firstOrSubsequent : ltv > 90 ? 0.015 : 0.0125)
  }
  return 0
}

export function upFrontFeeLabel (loanType) {
  if (loanType === 'fha') return 'Up-front MIP (financed)'
  if (loanType === 'va') return 'VA funding fee (financed)'
  if (loanType === 'usda') return 'USDA guarantee fee (financed)'
  return 'Up-front fee'
}

// FHA annual MIP rate (ML 2023-05). Tiers key off the base loan amount (not the price) against
// the high-balance cutoff, then term and base LTV, for purchases and refis alike. There is no
// zero-MIP tier: the short-term, low-LTV exemption CGS still applies ended in 2013.
function fhaAnnualRate (base, baseLtv, term) {
  if (base <= FHA_HIGH_BALANCE_CUTOFF) {
    return term > 180 ? (baseLtv > 95 ? 0.0055 : 0.005) : (baseLtv > 90 ? 0.004 : 0.0015)
  }
  return term > 180
    ? (baseLtv > 95 ? 0.0075 : 0.007)
    : (baseLtv > 90 ? 0.0065 : baseLtv > 78 ? 0.004 : 0.0015)
}

/*
  Monthly mortgage insurance for payments 1..k, off the scheduled balances in `rows`: the
  fixed-rate schedule, or the product's own schedule for an ARM, GPM or GEM. Conventional is
  NOT derived: it is the quoted `monthlyMI`.

  FHA: each policy year's premium is the annual rate on the average of that year's 12
  month-start balances of the base loan (total loan / (1 + UFMIP)), per HUD's annual MIP
  calculation method. Paid for 11 years when the base LTV is 90 or less, otherwise the full
  term (ML 2013-04, Handbook 4000.1).
  USDA: 0.35% of the year's average scheduled balance of the total loan, full term (HB-1-3555).
  Conventional: until the scheduled balance first reaches 78% of the original value or the
  term's midpoint, whichever is earlier; none if the loan starts at 78% or below (Homeowners
  Protection Act, 12 U.S.C. 4902(b),(c)).
  No value entered: FHA and conventional run the full term.
*/
function convMiWaived (loanAmount, value) {
  return value > 0 && loanAmount <= value * 0.78
}

function miSchedule ({ loanType, loanAmount, baseLoanAmount, baseLtv, value, termMonths, rows, monthlyMI }) {
  const n = termMonths
  const startBalance = m => (m === 0 ? loanAmount : (rows[m - 1] ? rows[m - 1].balance : 0))

  if (loanType === 'fha' || loanType === 'usda') {
    const annual = loanType === 'fha'
      ? fhaAnnualRate(baseLoanAmount, baseLtv, n) / (1 + FHA_UPFRONT)
      : USDA_MONTHLY
    const months = loanType === 'fha' && value > 0 && baseLtv <= 90 ? Math.min(132, n) : n
    const out = []
    for (let start = 0; start < months; start += 12) {
      const end = Math.min(start + 12, n)
      // Averaged over the months that still carry a balance, so a loan paid off mid-year (GEM)
      // is not charged on the zeros after payoff.
      let sum = 0
      let count = 0
      for (let m = start; m < end; m++) {
        const b = startBalance(m)
        if (b > 0) { sum += b; count++ }
      }
      const monthly = count ? (sum / count) * annual / 12 : 0
      for (let m = start; m < Math.min(end, months); m++) out.push(monthly)
    }
    return out
  }

  if (loanType === 'conv') {
    const mi = num(monthlyMI)
    if (!(value > 0)) return new Array(n).fill(mi)
    if (convMiWaived(loanAmount, value)) return []
    const floor = value * 0.78
    const out = []
    for (let k = 1; k <= n / 2 && startBalance(k - 1) > floor; k++) out.push(mi)
    return out
  }
  return []
}

/*
  APR by bisection on the monthly rate that discounts the payment stream back to the amount
  financed (Reg Z Appendix J). The stream is P&I plus each month's MI, because mortgage
  insurance is a finance charge (Reg Z 1026.4(b)(5)).

  CGS V1's calculateAPR was `(loanAmount * noteRate) * (term / 12)` divided about, and the
  source carries two "I need Clarification on how GS calculates it" comments against it. This
  returns the note rate exactly when there are no fees and no MI, which is the sanity check
  worth keeping.
*/
export function solveApr ({ loanAmount, payment, termMonths, fees, mi }) {
  const principal = num(loanAmount)
  const pay = num(payment)
  const n = num(termMonths)
  const financed = principal - num(fees)
  if (!(principal > 0 && pay > 0 && n > 0 && financed > 0)) return 0

  let low = 0
  let high = 1
  let r = 0
  for (let i = 0; i < 200; i++) {
    r = (low + high) / 2
    if (r <= 0) break
    let pv = pay * ((1 - Math.pow(1 + r, -n)) / r)
    for (let k = 0; k < mi.length; k++) pv += mi[k] / Math.pow(1 + r, k + 1)
    const diff = pv - financed
    if (Math.abs(diff) < 0.01) break
    if (diff > 0) low = r
    else high = r
  }
  return r * 12 * 100
}

// Month-by-month schedule. The final payment absorbs whatever is left rather than overshooting
// into a negative balance. Capped at the term, so a payment too small to amortize returns the
// rows it managed rather than spinning.
export function buildAmortization ({ loanAmount, annualPct, termMonths, payment }) {
  const r = num(annualPct) / 100 / 12
  const n = Math.round(num(termMonths))
  const pay = num(payment)
  if (!(pay > 0 && n > 0)) return []

  const rows = []
  let balance = num(loanAmount)
  for (let i = 0; i < n && balance > 0.01; i++) {
    const interest = balance * r
    let principal = pay - interest
    if (principal <= 0) break     // the payment does not even cover interest
    if (principal >= balance) {
      principal = balance
      balance = 0
    } else {
      balance -= principal
    }
    rows.push({ interest, principal, balance })
  }
  return rows
}

export function totalInterest (rows) {
  return (rows || []).reduce((s, row) => s + row.interest, 0)
}

/*
  APR for a payment that changes: the same bisection as solveApr, on each month's own P&I.
  For an ARM the stream is the composite one, at the index in effect at consummation held for
  the whole term (Reg Z 12 CFR 1026.17(c)(1), comment 10). MI stops with the last payment.
*/
function solveAprStream ({ financed, payments, mi }) {
  if (!(financed > 0 && payments.length)) return 0

  let low = 0
  let high = 1
  let r = 0
  for (let i = 0; i < 200; i++) {
    r = (low + high) / 2
    if (r <= 0) break
    let pv = 0
    let discount = 1
    for (let k = 0; k < payments.length; k++) {
      discount /= 1 + r
      pv += (payments[k] + (mi[k] || 0)) * discount
    }
    const diff = pv - financed
    if (Math.abs(diff) < 0.01) break
    if (diff > 0) low = r
    else high = r
  }
  return r * 12 * 100
}

// Unlike buildAmortization this never stops on a payment that does not cover the interest: a
// GPM's early years grow the balance, and the later payments are what pay it back. It stops
// early only on payoff, and the final payment absorbs whatever is left.
function buildSchedule (loanAmount, termMonths, next) {
  const rows = []
  let balance = loanAmount
  for (let m = 1; m <= termMonths && balance > 0.01; m++) {
    const { rate, payment } = next(m, balance)
    const interest = balance * rate / 100 / 12
    let principal = payment - interest
    let paid = payment
    if (principal >= balance || m === termMonths) {
      principal = balance
      paid = interest + balance
    }
    balance -= principal
    rows.push({ rate, payment: paid, interest, principal, balance })
  }
  return rows
}

// At each reset the rate moves to the fully indexed rate, at most the cap for that reset either
// way, then inside start + lifetime cap and never below the margin; the payment re-amortizes
// the balance over the months left.
function armSchedule ({ loanAmount, termMonths, startPct, product, indexPct, marginPct }) {
  const target = fullyIndexedRate(indexPct, marginPct)
  let rate = startPct
  let payment = pmt(loanAmount, rate, termMonths)
  return buildSchedule(loanAmount, termMonths, (m, balance) => {
    const sinceFirstReset = m - 1 - product.fixedMonths
    if (sinceFirstReset >= 0 && sinceFirstReset % ARM_RESET_MONTHS === 0) {
      const cap = sinceFirstReset === 0 ? product.initialCap : product.periodicCap
      rate = Math.min(Math.max(target, rate - cap), rate + cap)
      rate = Math.max(Math.min(rate, startPct + product.lifetimeCap), marginPct)
      payment = pmt(balance, rate, termMonths - m + 1)
    }
    return { rate, payment }
  })
}

// The first payment is solved so that the level payment after the graduation years pays the
// loan off exactly at term; pmt() would leave a balance.
function gpmSchedule ({ loanAmount, termMonths, annualPct, risePct, years }) {
  const r = annualPct / 100 / 12
  const step = m => Math.pow(1 + risePct / 100, Math.min(Math.floor((m - 1) / 12), years))
  let pv = 0
  for (let m = 1; m <= termMonths; m++) pv += step(m) / Math.pow(1 + r, m)
  const first = loanAmount / pv
  return buildSchedule(loanAmount, termMonths, m => ({ rate: annualPct, payment: first * step(m) }))
}

function gemSchedule ({ loanAmount, termMonths, annualPct, risePct }) {
  const first = pmt(loanAmount, annualPct, termMonths)
  return buildSchedule(loanAmount, termMonths, m => ({
    rate: annualPct,
    payment: first * Math.pow(1 + risePct / 100, Math.floor((m - 1) / 12))
  }))
}

// An index or margin that is not a number from 0 to 20% leaves the ARM unmodelled, as a blank
// index does, rather than quietly pricing whatever num() makes of it.
function armTermsError (input) {
  if (input.loanAmort !== 'Adjustable Rate') return ''
  const bad = v => {
    const n = Number(String(v).trim())
    return hasValue(v) && !(n >= 0 && n <= ARM_MAX_PCT)
  }
  if (bad(input.indexPct)) return `The index must be a number from 0% to ${ARM_MAX_PCT}%.`
  if (bad(input.marginPct)) return `The margin must be a number from 0% to ${ARM_MAX_PCT}%.`
  return ''
}

// "7/6 ARM SOFR (5/1/5), index 3.88586%, margin 2.750%" for the copied summaries.
export function armTermsLabel (input) {
  const product = ARM_PRODUCTS.find(p => p.key === input.armProduct) ||
    ARM_PRODUCTS.find(p => p.key === ARM_DEFAULT_PRODUCT)
  const index = hasValue(input.indexPct) ? String(input.indexPct).trim() + '%' : 'not entered'
  return `${product.label}, index ${index}, margin ${pct(orDefault(input.marginPct, ARM_DEFAULT_MARGIN))}`
}

// The schedule for a payment that changes, or null when the scenario is priced as fixed:
// Other, an ARM with no usable index or margin, or an ARM on a government loan (its caps are
// not these).
function variableSchedule (input, loanAmount, termMonths, annualPct) {
  switch (input.loanAmort) {
    case 'Adjustable Rate': {
      if (!amortTypesFor(input.loanType).includes(input.loanAmort) || !hasValue(input.indexPct) || armTermsError(input)) return null
      const product = ARM_PRODUCTS.find(p => p.key === input.armProduct) ||
        ARM_PRODUCTS.find(p => p.key === ARM_DEFAULT_PRODUCT)
      return armSchedule({
        loanAmount,
        termMonths,
        startPct: annualPct,
        product,
        indexPct: input.indexPct,
        marginPct: orDefault(input.marginPct, ARM_DEFAULT_MARGIN)
      })
    }
    case 'Graduated Payment':
      return gpmSchedule({
        loanAmount,
        termMonths,
        annualPct,
        risePct: orDefault(input.gpmRatePct, GPM_DEFAULT_RATE),
        years: Math.round(orDefault(input.gpmYears, GPM_DEFAULT_YEARS))
      })
    case 'Growing Equity Mortgage':
      return gemSchedule({ loanAmount, termMonths, annualPct, risePct: orDefault(input.gemRatePct, GEM_DEFAULT_RATE) })
    default:
      return null
  }
}

// GPM / GEM inputs outside these limits leave the scenario unpriced rather than quoting a
// payment stream no one offers. Blank fields take the defaults.
function gradedTermsError (input, termMonths) {
  const isGpm = input.loanAmort === 'Graduated Payment'
  if (!isGpm && input.loanAmort !== 'Growing Equity Mortgage') return ''
  const rise = isGpm ? orDefault(input.gpmRatePct, GPM_DEFAULT_RATE) : orDefault(input.gemRatePct, GEM_DEFAULT_RATE)
  if (rise < 0 || rise > GRADED_MAX_RISE) return `The annual payment increase must be between 0% and ${GRADED_MAX_RISE}%.`
  if (!isGpm || !(termMonths > 0)) return ''
  const years = Math.round(orDefault(input.gpmYears, GPM_DEFAULT_YEARS))
  const maxYears = Math.floor(termMonths / 12)
  if (maxYears < 1) return 'A graduated payment needs a term of at least 12 months.'
  if (years < 1 || years > maxYears) return `The increase years must be between 1 and ${maxYears}.`
  return ''
}

/*
  One call that turns a set of inputs into every derived figure, so the two calculators cannot
  disagree about the order things are computed in. Inputs are the raw strings the form holds.
*/
export function priceScenario (input) {
  const isBuy = input.loanPurpose === 'Buy'
  const price = num(input.purchasePrice)
  const termMonths = Math.round(num(input.term))
  const annualPct = num(input.noteRatePct)

  // The Cash out field only hides when the refi type changes; it keeps its value.
  const baseLoanAmount = isBuy
    ? Math.max(0, price - num(input.downPayment))
    : num(input.amountOwed) + (input.refiType === 'Cash Out' ? num(input.cashOut) : 0)

  const baseLtv = price > 0 ? roundLtv((baseLoanAmount / price) * 100) : 0

  const fee = upFrontFee({
    loanType: input.loanType,
    isBuy,
    refiType: input.refiType,
    vaType: input.vaType,
    firstTime: input.firstTime,
    baseLoanAmount,
    baseLtv
  })

  // Conventional finances nothing up front; the others roll the fee into the loan.
  const loanAmount = baseLoanAmount + fee
  const ltv = price > 0 ? (loanAmount / price) * 100 : 0

  // A BLANK rate field is not a 0% loan. Without this, pmt()'s zero-rate branch straight-lines
  // the principal and the page shows a confident payment before anyone entered a rate.
  const termsError = gradedTermsError(input, termMonths)
  const armError = armTermsError(input)
  const isPriced = hasValue(input.noteRatePct) && termMonths > 0 && !termsError
  const schedule = isPriced ? variableSchedule(input, loanAmount, termMonths, annualPct) : null

  // The displayed P&I for a payment that changes is the first month's.
  let pandi = 0
  let rows = []
  if (schedule) {
    rows = schedule
    pandi = rows.length ? rows[0].payment : 0
  } else if (isPriced) {
    pandi = pmt(loanAmount, annualPct, termMonths)
    rows = buildAmortization({ loanAmount, annualPct, termMonths, payment: pandi })
  }
  const mi = miSchedule({
    loanType: input.loanType,
    loanAmount,
    baseLoanAmount,
    baseLtv,
    value: price,
    termMonths,
    rows,
    monthlyMI: input.monthlyMI
  })

  // The displayed FHA/USDA figure is year one of the same schedule; unpriced there is no
  // schedule to average, so it falls back to the base loan.
  const miWaived = input.loanType === 'conv' && convMiWaived(loanAmount, price)
  let pmi = num(input.monthlyMI)
  if (input.loanType === 'va' || miWaived) pmi = 0
  else if (input.loanType === 'fha') pmi = isPriced ? mi[0] || 0 : baseLoanAmount * fhaAnnualRate(baseLoanAmount, baseLtv, termMonths) / 12
  else if (input.loanType === 'usda') pmi = isPriced ? mi[0] || 0 : baseLoanAmount * USDA_MONTHLY / 12

  const taxes = num(input.taxes)
  const insurance = num(input.insurance)
  const hoa = num(input.hoa)
  const totalPayment = pandi + pmi + taxes + insurance + hoa

  // A type priced as fixed (Other, an ARM without its index) has no schedule worth drawing;
  // its APR is an estimate on the initial payment, and `aprEstimate` lets the page say so.
  const amortization = input.loanAmort === 'Fixed' || schedule ? rows : []
  const fees = num(input.additionalFees) + fee
  const apr = schedule
    ? solveAprStream({ financed: loanAmount - fees, payments: rows.map(row => row.payment), mi })
    : solveApr({ loanAmount, payment: pandi, termMonths, fees, mi })

  return {
    isBuy,
    isPriced,
    termsError,
    armError,
    baseLoanAmount,
    baseLtv,
    upFrontFee: fee,
    upFrontFeeLabel: upFrontFeeLabel(input.loanType),
    loanAmount,
    ltv,
    termMonths,
    annualPct,
    pandi,
    pmi,
    miWaived,
    taxes,
    insurance,
    hoa,
    totalPayment,
    apr,
    aprEstimate: isPriced && !!input.loanAmort && input.loanAmort !== 'Fixed' && !schedule,
    amortization,
    totalInterestPaid: totalInterest(amortization)
  }
}

// "360" -> "30 Years", "342" -> "28.5 Years". CGS builds the default scenario name this way.
export function termLabel (months) {
  const m = Math.round(num(months))
  if (!m) return '0 Years'
  return (m % 12 === 0 ? String(m / 12) : (m / 12).toFixed(1)) + ' Years'
}

// A negative (a GPM's early principal) reads "-$12.34", not "$-12.34".
export function money (n) {
  const v = Number(n) || 0
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return (v < 0 && s !== '0.00' ? '-$' : '$') + s
}

export function pct (n) {
  return (Number(n) || 0).toFixed(3) + '%'
}

/*
  Temporary buydown — ported from CGS2022 Scripts/Calculator/buydownCalculator.vue.

  A "3-2-1" takes 3 points off the note rate in year one, 2 in year two, 1 in year three, then
  the borrower pays the full note-rate payment. The seller/lender funds the difference up front:
  each bought-down year's monthly shortfall x 12, summed, is the buydown fund (escrow).

  Same method as CGS: every year's payment is a full-term amortizing payment at the reduced
  rate, on the original balance. Where this departs from CGS:
  - CGS rounds every figure to whole dollars for display, and its fund is the sum of the
    UNROUNDED subsidies. Both are kept to the cent here; the fund is still summed unrounded.
  - CGS's getMonthlyPayment divides by zero on a 0% year, and happily prices a negative rate
    when the reduction exceeds the note rate. pmt() straight-lines 0%, and a reduction at or
    above the note rate is reported back as an error instead of priced.
  - "1-0" is a one-year buydown; the zero year is dropped, exactly as CGS's filter does.
*/
export const BUYDOWN_TYPES = ['3-2-1', '2-1', '1-1', '1-0']

export function buydownSchedule ({ loanAmount, noteRatePct, termMonths, type }) {
  const principal = num(loanAmount)
  const rate = num(noteRatePct)
  const n = Math.round(num(termMonths))
  const reductions = String(type || '').split('-').map(Number).filter(r => r > 0)

  const isPriced = principal > 0 && hasValue(noteRatePct) && n > 0
  const fullPayment = isPriced ? pmt(principal, rate, n) : 0

  const years = reductions.map((reduction, i) => {
    const yearRate = rate - reduction
    const payment = isPriced && yearRate >= 0 ? pmt(principal, yearRate, n) : 0
    const subsidy = payment > 0 ? fullPayment - payment : 0
    return { year: i + 1, reduction, rate: yearRate, payment, subsidy, annualSubsidy: subsidy * 12 }
  })

  const error = isPriced && reductions.length && rate - reductions[0] < 0
    ? `A ${type} buydown takes ${reductions[0]} points off in year one, which is more than the ${rate.toFixed(3)}% note rate.`
    : ''

  return {
    isPriced: isPriced && !error,
    error,
    fullPayment,
    years,
    buydownFund: error ? 0 : years.reduce((s, y) => s + y.annualSubsidy, 0)
  }
}
